import { type ReactElement, useEffect, useMemo, useState } from "react";
import { Dimmer, Loader, Segment } from "semantic-ui-react";

import AccountPacksList from "./AccountPacksList/AccountPacksList";
import { type AccountPack, type AccountPackLicense } from "../../types";
import PackAssignmentModal from "features/Accounts/PackAssignmentModal/PackAssignmentModal";
import ExtendLicensesModal from "features/Accounts/PackAssignmentModal/extendLicensesModal/ExtendLicensesModal";
import { type Filters } from "utils/queryUtils";
import { type AccountAvailablePackOverview, type AssignAccountPack } from "features/Accounts/PackAssignmentModal/types";
import { type FiltersMap } from "utils/filterUtils";
import { type PackFilter } from "./types/models";
import { type SearchState } from "../../../Application/slices/createSearchSlice";
import { type RolePermissions, ViewType } from "../../../../enums";
import { type GetPacksRequest, type PacksRequest } from "../../../Licensing/Packs/types/requests";
import { type AccountPacksGridFetchType } from "features/Accounts/state/thunks/accountPacksThunk";
import Observable from "../../../../utils/Observable";
import { type AccountPacksOverviewState } from "features/Accounts/state/slices/accountPacksSlice";
import EditLicensingModal from "features/Accounts/PackAssignmentModal/editLicensingModal/EditLicensingModal";
import { useSearchParams } from "react-router";
import PackRequestTypes from "../../../../enums/packRequestTypes";

import "./editPacks.scss";
import { type AppDispatch } from "features/Application/globaltypes/redux";

export interface EditPacksProps {
  accountId: number;
  selectedAccountId: number;
  packsList: {
    items: Array<AccountPack>;
    isLoading: boolean;
    itemsCount: number;
  };
  packsGrid: AccountPacksOverviewState;
  renderAddPacksButton?: () => ReactElement;
  onFetchPacks: (
    accountId: number,
    request: GetPacksRequest,
    filterParams: FiltersMap,
  ) => (dispatch: AppDispatch) => Promise<void>;
  onResetPacksList: () => void;
  onFetchPacksGrid: (requestData: PacksRequest) => AccountPacksGridFetchType;
  onResetPacksGrid: () => void;
  onCloseAddPacksModal: () => void;
  onConfirmAddPacksModal: (packs: AssignAccountPack[], selectedViewType: ViewType) => void;
  onConfirmEditLicensingModal: (accountPackLicense: AccountPackLicense, selectedViewType: ViewType) => void;
  onConfirmExtendLicensesModal: (packs: AssignAccountPack[], selectedViewType: ViewType) => void;
  onConfirmRestorePackModal: (packs: AssignAccountPack[], selectedViewType: ViewType) => void;
  onConfirmConvertTrialPackModal: (packs: AssignAccountPack[], selectedViewType: ViewType) => void;
  showAddPacksModal: boolean;
  isAccountInfoLoading: boolean;
  canAddPack: boolean;
  hasPurchasePower: boolean;
  addPacksModal: {
    items: Array<AccountAvailablePackOverview>;
    isLoading: boolean;
    itemsCount: number;
    areAllLoaded: boolean;
  };
  loadPacksForModal: (assignedAccountId: number, skip: number, orderBy: string, filters?: Filters) => void;
  loadPackForModal: (assignedAccountId: number, packIds: number[]) => void;
  resetModalGrid: () => void;
  modalFilter: PackFilter;
  accountPacksFilter: PackFilter;
  setPacksModalSearch: (searchTerm: string) => void;
  packsModalSearch: SearchState;
  isPacksModalFiltered: boolean;
  accountPacksSearch?: SearchState;
  setAccountPacksSearch: (term: string) => { payload: string; type: string };
  triggerRefreshPacksGrid: (viewType: ViewType) => void;
  isAccountHome: boolean;
  permissionsToAccount: RolePermissions[];
  enabled?: boolean;
}

export default function EditPacks(props: EditPacksProps) {
  const [selectedViewType, setSelectedViewType] = useState(ViewType.LIST);
  const [searchParams] = useSearchParams();
  const requestTypeId = searchParams.get("requestTypeId");
  const packId = searchParams.get("packId");
  const shouldShowPackAssignmentModal = !requestTypeId || Number(requestTypeId) === PackRequestTypes.RequestPack;
  const [assignPredefinedPackId, setAssignPredefinedPackId] = useState(shouldShowPackAssignmentModal ? packId : null);
  const [packPricingTypeId, setPackPricingTypeId] = useState<number | undefined>(undefined);

  const onExtendLicensesClickObserver = useMemo(() => new Observable(), []);
  const onEditLicensingClickObserver = useMemo(() => new Observable(), []);
  const onRestorePackClickObserver = useMemo(() => new Observable(), []);
  const onConvertTrialLicensingClickObserver = useMemo(() => new Observable(), []);

  useEffect(() => {
    if (!props.isAccountInfoLoading && requestTypeId && packId) {
      const requestType = Number(requestTypeId);
      const packIdNum = Number(packId);

      const notificationsActions: Record<number, () => void> = {
        [PackRequestTypes.RequestPackRenewal]: () =>
          onExtendLicensesClickObserver.notify(packIdNum, props.selectedAccountId, true, false),
        [PackRequestTypes.RequestExtraLicenses]: () =>
          onEditLicensingClickObserver.notify(packIdNum, props.selectedAccountId, true),
        [PackRequestTypes.RequestTrialConvert]: () =>
          onConvertTrialLicensingClickObserver.notify(packIdNum, props.selectedAccountId, true, true),
      };

      notificationsActions[requestType]?.();
    }
  }, [
    props.isAccountInfoLoading,
    props.selectedAccountId,
    requestTypeId,
    packId,
    onExtendLicensesClickObserver,
    onEditLicensingClickObserver,
    onConvertTrialLicensingClickObserver,
  ]);

  const loadPacksList = (skip: number, top: number, order: string, appliedFilter: any, searchTerm?: string) => {
    const request: GetPacksRequest = {
      skip,
      top,
      orderBy: order,
      searchTerm,
    };
    props.onFetchPacks(props.accountId, request, appliedFilter);
  };

  const loadPacksGrid = (skip: number, order: string, appliedFilter: any, searchTerm?: string) => {
    return props.onFetchPacksGrid({
      accountId: props.accountId,
      skip,
      orderBy: order,
      filters: appliedFilter,
      searchTerm,
    });
  };

  const handleExtendLicensesClick = (
    id: number,
    title: string,
    thumbnailImageUrl: string,
    packPricingTypeId?: number,
  ) => {
    setPackPricingTypeId(packPricingTypeId);
    onExtendLicensesClickObserver.notify(id, props.selectedAccountId);
  };

  const handleClosingPackAssigmentModal = () => {
    if (assignPredefinedPackId) {
      setAssignPredefinedPackId(null);
    }
  };

  const renderPacksAddModal = () => {
    const {
      onCloseAddPacksModal,
      onConfirmAddPacksModal,
      accountId,
      selectedAccountId,
      addPacksModal,
      packsModalSearch,
      isPacksModalFiltered,
      loadPacksForModal,
      loadPackForModal,
      setPacksModalSearch,
      resetModalGrid: resetGrid,
    } = props;
    const { applyFilter, resetFilter, fetchFilterOptions, appliedFilter, filterOptions } = props.modalFilter;

    return (
      <PackAssignmentModal
        onConfirm={(packs: AssignAccountPack[], viewType: ViewType) => {
          onConfirmAddPacksModal(packs, viewType);
          handleClosingPackAssigmentModal();
        }}
        onCancel={() => {
          onCloseAddPacksModal();
          handleClosingPackAssigmentModal();
        }}
        showModal
        selectAccountId={selectedAccountId}
        accountId={accountId}
        loadPacksForModal={loadPacksForModal}
        loadPackForModal={loadPackForModal}
        appliedFilter={appliedFilter}
        applyFilter={applyFilter}
        filterOptions={filterOptions}
        isLoading={addPacksModal.isLoading}
        areAllLoaded={addPacksModal.areAllLoaded}
        items={addPacksModal.items}
        itemsCount={addPacksModal.itemsCount}
        resetFilter={resetFilter}
        fetchFilterOptions={fetchFilterOptions}
        resetGrid={resetGrid}
        packsModalSearch={packsModalSearch}
        setPacksModalSearch={setPacksModalSearch}
        selectedViewType={selectedViewType}
        isPacksModalFiltered={isPacksModalFiltered}
        predefinedPackIds={assignPredefinedPackId ? [Number(assignPredefinedPackId)] : []}
      />
    );
  };

  const renderExtendLicensesModal = () => {
    const { onConfirmExtendLicensesModal, selectedAccountId } = props;
    return (
      <ExtendLicensesModal
        onConfirm={onConfirmExtendLicensesModal}
        selectedViewType={selectedViewType}
        selectedAccountId={selectedAccountId}
        showModalObserver={onExtendLicensesClickObserver}
        header="Edit Licensing"
        packPricingTypeId={packPricingTypeId}
      />
    );
  };

  const handleRestorePackClick = (id: number, title: string, thumbnailImageUrl: string, packPricingTypeId?: number) => {
    setPackPricingTypeId(packPricingTypeId);
    onRestorePackClickObserver.notify(id, props.selectedAccountId);
  };

  const renderRestorePackModal = () => {
    const { onConfirmRestorePackModal, selectedAccountId } = props;
    return (
      <ExtendLicensesModal
        onConfirm={onConfirmRestorePackModal}
        selectedViewType={selectedViewType}
        selectedAccountId={selectedAccountId}
        showModalObserver={onRestorePackClickObserver}
        header="Unarchive & Restore Licenses"
        isRestore
        packPricingTypeId={packPricingTypeId}
      />
    );
  };

  const handleEditLicensingClick = (id: number, packPricingTypeId?: number) => {
    setPackPricingTypeId(packPricingTypeId);
    onEditLicensingClickObserver.notify(id, props.selectedAccountId);
  };

  const renderEditLicensingModal = () => {
    const { onConfirmEditLicensingModal } = props;
    return (
      <EditLicensingModal
        onConfirm={onConfirmEditLicensingModal}
        selectedViewType={selectedViewType}
        showModalObserver={onEditLicensingClickObserver}
      />
    );
  };

  const handleConvertTrialLicensingClick = (id: number, packPricingTypeId?: number) => {
    setPackPricingTypeId(packPricingTypeId);
    onConvertTrialLicensingClickObserver.notify(id, props.selectedAccountId);
  };

  const renderConvertTrialLicensingModal = () => {
    const { onConfirmConvertTrialPackModal, selectedAccountId } = props;
    return (
      <ExtendLicensesModal
        onConfirm={onConfirmConvertTrialPackModal}
        selectedViewType={selectedViewType}
        selectedAccountId={selectedAccountId}
        showModalObserver={onConvertTrialLicensingClickObserver}
        header="Edit Licensing"
        packPricingTypeId={packPricingTypeId}
      />
    );
  };

  const renderAccountPacks = () => {
    const {
      renderAddPacksButton,
      onResetPacksList,
      onResetPacksGrid,
      packsList,
      packsGrid,
      canAddPack,
      showAddPacksModal,
      setAccountPacksSearch,
      accountPacksSearch,
      permissionsToAccount,
      hasPurchasePower,
      enabled = true,
    } = props;

    return (
      <>
        <AccountPacksList
          enabled={enabled}
          packsList={packsList}
          packsGrid={packsGrid}
          loadListItems={loadPacksList}
          resetListItems={onResetPacksList}
          loadGridItems={loadPacksGrid}
          resetGridItems={onResetPacksGrid}
          renderAddPacksButton={renderAddPacksButton}
          handleEditLicensingClick={handleEditLicensingClick}
          handleExtendLicensesClick={handleExtendLicensesClick}
          handleRestorePackClick={handleRestorePackClick}
          handleConvertTrialLicensingClick={handleConvertTrialLicensingClick}
          canAddPack={canAddPack}
          selectedViewType={selectedViewType}
          setSelectedViewType={setSelectedViewType}
          searchState={accountPacksSearch}
          setSearch={setAccountPacksSearch}
          triggerRefreshPacksGrid={props.triggerRefreshPacksGrid}
          isAccountHome={props.isAccountHome}
          selectedAccountId={props.selectedAccountId}
          permissionsToAccount={permissionsToAccount}
          hasPurchasePower={hasPurchasePower}
        />
        {showAddPacksModal && renderPacksAddModal()}
        {assignPredefinedPackId && renderPacksAddModal()}
        {renderExtendLicensesModal()}
        {renderEditLicensingModal()}
        {renderRestorePackModal()}
        {renderConvertTrialLicensingModal()}
      </>
    );
  };

  return props.isAccountInfoLoading ? (
    <Dimmer.Dimmable className="edit-packs-dimmer" as={Segment} dimmed={props.isAccountInfoLoading}>
      <Dimmer active={props.isAccountInfoLoading} inverted>
        <Loader>Loading</Loader>
      </Dimmer>
    </Dimmer.Dimmable>
  ) : (
    renderAccountPacks()
  );
}
